import axios from 'axios';
import { message } from 'antd';
import { notifyAuthExpiredAndRedirect } from '@/utils/clearClientAuthSession';
import { getBrowserApiOrigin } from '@/utils/browserApiUrl';
function normalizeApiOrigin() {
    return getBrowserApiOrigin();
}
export const API_ORIGIN = normalizeApiOrigin();
// 创建axios实例
const api = axios.create({
    baseURL: API_ORIGIN,
    timeout: 30000, // 30秒超时
    headers: {
        'Content-Type': 'application/json',
    },
});
// 请求拦截器
api.interceptors.request.use((config) => {
    // 统一将相对路径映射到 /api/*
    if (config.url && !/^https?:\/\//i.test(config.url)) {
        config.url = config.url.startsWith('/api')
            ? config.url
            : `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    // 从本地存储获取token
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // multipart 需由浏览器自动带 boundary，不能固定为 application/json
    if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
    }
    // 添加请求时间戳（防止缓存）
    if (config.method?.toLowerCase() === 'get') {
        config.params = {
            ...config.params,
            _t: Date.now(),
        };
    }
    const finalUrl = `${config.baseURL || ''}${config.url || ''}`;
    console.log('[API] request', config.method?.toUpperCase(), finalUrl);
    return config;
}, (error) => {
    console.error('请求配置错误:', error);
    return Promise.reject(error);
});
/** 订单详情等后台读取：调用方自己处理失败，403 不再弹全局「权限不足」。 */
export function quietRequest(config) {
    return { ...(config || {}), skipForbiddenToast: true };
}
function skipForbiddenToast(config) {
    return Boolean(config && typeof config === 'object' && config.skipForbiddenToast);
}
// 响应拦截器
api.interceptors.response.use((response) => {
    // 处理成功响应
    const { data } = response;
    // 如果返回的是Blob（文件下载），直接返回
    if (data instanceof Blob) {
        return response;
    }
    // 处理业务逻辑错误
    if (data.code && data.code !== 200) {
        const errorMsg = data.message || '请求失败';
        if (data.code === 401) {
            const reqUrl = String((response.config && response.config.url) || '');
            if (!reqUrl.includes('/auth/login') && !reqUrl.includes('/auth/refresh-token')) {
                notifyAuthExpiredAndRedirect();
            }
            else if (!skipForbiddenToast(response.config)) {
                message.error(errorMsg);
            }
            return Promise.reject(new Error(errorMsg));
        }
        if (data.code === 403 && skipForbiddenToast(response.config)) {
            return Promise.reject(new Error(errorMsg));
        }
        if (!skipForbiddenToast(response.config) || data.code !== 403) {
            message.error(errorMsg);
        }
        return Promise.reject(new Error(errorMsg));
    }
    return data;
}, (error) => {
    // 处理HTTP错误
    if (error.response) {
        const { status, data } = error.response;
        const quiet403 = status === 403 && skipForbiddenToast(error.config);
        switch (status) {
            case 400:
                message.error(data?.message || '请求参数错误');
                break;
            case 401: {
                const reqUrl = String(error.config?.url || '');
                if (reqUrl.includes('/auth/login') || reqUrl.includes('/auth/refresh-token')) {
                    message.error(data?.message || '登录失败');
                    break;
                }
                notifyAuthExpiredAndRedirect();
                break;
            }
            case 403:
                if (!quiet403) {
                    message.error(data?.message || data?.detail || '权限不足，无法访问');
                }
                break;
            case 409:
                message.warning(data?.message || data?.detail || '请求冲突');
                break;
            case 404:
                message.error('请求的资源不存在');
                break;
            case 500:
                message.error('服务器内部错误');
                break;
            case 502:
            case 503:
            case 504:
                message.error('服务暂时不可用，请稍后重试');
                break;
            default:
                message.error(data?.message || data?.detail || `请求失败 (${status})`);
        }
    }
    else if (error.request) {
        // 请求已发出但没有收到响应
        message.error('网络请求失败，请检查后端地址、反向代理与CORS/认证配置');
    }
    else {
        // 请求配置错误
        message.error('请求配置错误');
    }
    console.error('API请求错误:', error);
    return Promise.reject(error);
});
// 文件上传配置
export const uploadConfig = {
    maxSize: 50 * 1024 * 1024, // 50MB
    allowedTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/zip',
        'application/x-rar-compressed',
        'application/x-7z-compressed',
        'model/stl',
        'model/obj',
        'application/octet-stream', // 通用二进制文件
    ],
};
// 导出API实例
export default api;
